/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../../../../shared/stateful/stateful.component';
import {PncSeveralFullLotNumberModalState} from './models/pnc-several-full-lot-number-modal.state';
import {PncFullLotNumberChipModel} from './pnc-full-lot-number-chip/models/pnc-full-lot-number-chip.model';
import { v4 as uuid } from 'uuid';
import {ToastWrapperService} from '../../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {AlertNotificationSeverity} from '../../../../alert-notification-severity.model';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {GenericModalComponent} from '../../../../shared/generic-modal/generic-modal.component';
import {ConfirmModalComponent} from '../../../../shared/confirm-modal/confirm-modal/confirm-modal.component';

@Component({
    selector: 'app-pnc-several-full-lot-number-modal',
    templateUrl: './pnc-several-full-lot-number-modal.component.html',
    styleUrls: ['./pnc-several-full-lot-number-modal.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PncSeveralFullLotNumberModalComponent extends StatefulComponent<PncSeveralFullLotNumberModalState> implements OnInit {
    @Input() savedFullLotNumbersLength: number;
    @Input() fullLotNumbersMaxLimit: number;
    @Input() additionalFullLotNumbersMaxLimit: number;
    @Output() primaryEvent = new EventEmitter<string[]>();
    @Output() closeEvent = new EventEmitter<void>();
    @ViewChild('pasteAreaChips') pasteAreaChips: ElementRef;
    @ViewChild('genericModal') genericModal: GenericModalComponent;

    readonly pastAreaHeight = 164;
    // Subtracting 4 pixels from the pastAreaHeight at the beginning eliminates the need for the scroll bar to be displayed.
    private readonly initialPastAreaTextHeight = (this.pastAreaHeight - 4);
    private currentPastAreaTextHeight = this.initialPastAreaTextHeight;
    private readonly fullLotNumberRegex = /^[E|e]\w{4}\d{4}$/;
    private readonly maxFullLotNumbersAddOnToastMessage = 8;
    private readonly maxPastedCharsLimit = 1000;
    private readonly maxCharsLimitOnChip = 20;

    get scrollablePastAreaTextHeight(): string {
        return `${this.currentPastAreaTextHeight}px`;
    }

    get pastAreaTextHeightPlaceholder(): string {
        return this.state.fullLotNumbersChips.length ? '' : 'Paste Full LOT numbers';
    }

    get limitExceededErrorMessage(): string {
        return `The max of ${this.additionalFullLotNumbersMaxLimit} additional Full LOT numbers was reached.`;
    }

    get limitOfChipsInTextAreaExceeded(): boolean {
        return this.state.chipsInTextAreaLimit < this.state.fullLotNumbersChips.filter((elem) => elem.isValid).length;
    }

    constructor(private toast: ToastWrapperService, private modalService: NgbModal) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            fullLotNumbersChips: [],
            chipsInTextAreaLimit: this.fullLotNumbersMaxLimit - this.savedFullLotNumbersLength
        });
    }

    onChipDelete(chipId: string): void {
        this.storeState({
            fullLotNumbersChips: this.state.fullLotNumbersChips
                    .filter((elem) => elem.id !== chipId)
        });
        // At this point in time, the DOM has not been updated with the new chip(s) and hence the scroll height is not accurate.
        // Therefore, using setTimeout with a delay of 0 (or a very small number) allows the current thread of execution to finish and
        // gives Angular time to update the DOM before calling resizePastAreaTextHeight function.
        setTimeout(() => this.resizePastAreaTextHeight());
    }

    submit(): void {
        const validFullLotNumbers = this.state.fullLotNumbersChips
                .filter((elem) => elem.isValid)
                .map((elem) => elem.fullLotNumber)
                .slice(0, this.state.chipsInTextAreaLimit);
        this.primaryEvent.emit(validFullLotNumbers);

        let toastMessage = `${validFullLotNumbers.length} full LOT No. were successfully added!`;
        if (validFullLotNumbers.length <= this.maxFullLotNumbersAddOnToastMessage) {
            toastMessage = `Full LOT No. ${validFullLotNumbers.join(', ')} successfully added!`;
        }
        this.toast.success(toastMessage, 'Success');
    }

    isValid(): boolean {
        return !!this.state.fullLotNumbersChips.find((elem) => elem.isValid);
    }

    isChipWithinLimit(index: number): boolean {
        return this.state.chipsInTextAreaLimit >= this.state.fullLotNumbersChips.slice(0, index + 1)
                .filter((elem) => elem.isValid).length;
    }

    pasteHandler(event: ClipboardEvent): void {
        // Stop data actually being pasted into div
        event.stopPropagation();
        event.preventDefault();

        // Get pasted data via clipboard API
        // TypeScript 3.8.3 doesn't have typings for ClipboardEvent.clipboardData, so we get it from the Window object as an unknown type.
        // ClipboardEvent.clipboardData: https://developer.mozilla.org/en-US/docs/Web/API/ClipboardEvent/clipboardData
        const clipboardData = event.clipboardData || (window as any).clipboardData;
        const pastedData = clipboardData?.getData('Text');

        this.pasteValidations(pastedData);
    }

    private pasteValidations(pastedData: string): void {
        if (pastedData.length < this.maxPastedCharsLimit) {
            const fullLotNumbers = this.parsePastedDataByLineEndingCharacter(pastedData);
            if (fullLotNumbers.length) {
                this.generateFullLotNumberChips(fullLotNumbers);
                // At this point in time, the DOM has not been updated with the new chip(s) and hence the scroll height is not accurate.
                // Therefore, using setTimeout with a delay of 0 (or a very small number) allows the current thread of execution to finish
                // and gives Angular time to update the DOM before calling resizePastAreaTextHeight function.
                setTimeout(() => this.resizePastAreaTextHeight());
            }
        } else {
            this.genericModal.clickCloseButton();
            const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
            confirmModalInstance.modalConfiguration = {
                body: {
                    width: 420,
                    title: `The max of ${this.maxPastedCharsLimit} characters was reached.`
                },
                title: 'Error',
                secondaryActionTitle: 'Close',
                alertNotificationSeverity: AlertNotificationSeverity.ERROR
            };
        }
    }

    private parsePastedDataByLineEndingCharacter(data: string): string[] {
        // The regular expression /r?\n/ matches all possible line endings, whether it's just a newline character (\n), a carriage return
        // followed by a newline character (\r\n), or a standalone carriage return (\r).
        // Since it can contain strings that are empty lines, a filtering is done to exclude that
        return data?.split(/\r?\n|\s+/)
                ?.filter((elem) => elem.trim() !== '')
                ?.map((elem) => elem.trim()) ?? [];
    }

    private generateFullLotNumberChips(fullLotNumbers: string[]): void {
        const pastedChips: PncFullLotNumberChipModel[] = fullLotNumbers?.map((elem) => ({
            id: uuid(),
            fullLotNumber: elem.toUpperCase()
                    .slice(0, this.maxCharsLimitOnChip),
            isValid: new RegExp(this.fullLotNumberRegex)
                    .test(elem)
        })) ?? [];

        this.storeState({
            fullLotNumbersChips: this.state.fullLotNumbersChips.concat(pastedChips)
        });
    }

    private resizePastAreaTextHeight(): void {
        this.currentPastAreaTextHeight = Math.max(this.pasteAreaChips.nativeElement.scrollHeight, this.initialPastAreaTextHeight);
    }
}
